import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Me, Permission } from '@replay/shared';
import { api, ApiError } from './api.ts';

interface AuthState {
  me: Me | null;
  loading: boolean;
  can: (p: Permission) => boolean;
  refresh: () => Promise<unknown>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

const ME_KEY = 'replay.me';
function remember(me: Me | null) {
  try {
    if (me) localStorage.setItem(ME_KEY, JSON.stringify(me));
    else localStorage.removeItem(ME_KEY);
  } catch {
    /* storage unavailable */
  }
}
function recall(): Me | null {
  try {
    return JSON.parse(localStorage.getItem(ME_KEY) ?? 'null') as Me | null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        const me = await api<Me>('/api/me');
        remember(me);
        return me;
      } catch (e) {
        if (e instanceof ApiError && e.kind === 'auth') {
          remember(null);
          return null;
        }
        // Offline or server unreachable: keep using the last known profile so
        // games already on this device stay playable.
        if (e instanceof ApiError && e.retryable) {
          const cached = recall();
          if (cached) return cached;
        }
        throw e;
      }
    },
    staleTime: 60_000,
    retry: (n, e) => e instanceof ApiError && e.retryable && n < 3,
  });

  useEffect(() => {
    const onUnauth = () => {
      remember(null);
      qc.setQueryData(['me'], null);
    };
    window.addEventListener('replay:unauthenticated', onUnauth);
    return () => window.removeEventListener('replay:unauthenticated', onUnauth);
  }, [qc]);

  const can = useCallback((p: Permission) => !!q.data?.permissions.includes(p), [q.data]);
  const logout = useCallback(async () => {
    await api('/api/auth/logout', { method: 'POST' }).catch(() => {});
    remember(null);
    // Mark the session as gone on the live query (clearing the cache first would leave
    // mounted components bound to a removed query, still showing the old profile).
    qc.setQueryData(['me'], null);
    qc.removeQueries({ predicate: (query) => query.queryKey[0] !== 'me' });
    // Full reload on the login page: no user data, live connection or audio survives.
    window.location.replace('/login');
  }, [qc]);

  return <Ctx.Provider value={{ me: q.data ?? null, loading: q.isLoading, can, refresh: q.refetch, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('AuthProvider missing');
  return v;
}

export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('not signed in');
  return me;
}
